import { useCallback, useEffect, useState } from 'react'

import {
  PUSH_STATE_EVENT,
  dismissPushPrompt,
  getNotificationPermission,
  isPushOptedOut,
  isPushPromptDismissed,
  isPushSupported,
  notifyPushStateChange,
  setPushOptOut,
  teardownPushNotification,
} from 'utils/pushNotification'

const getStatus = ({ supported, permission, optedOut }) => {
  if (supported === null) {
    return 'loading'
  }
  if (!supported) {
    return 'unsupported'
  }
  if (permission === 'denied') {
    return 'denied'
  }
  if (permission === 'granted') {
    return optedOut ? 'opted_out' : 'granted'
  }
  return 'default'
}

/**
 * State izin web push perangkat ini + aksinya. Sumber kebenaran adalah browser
 * (izin Notification) dan storage lokal, sehingga banner, card pengaturan, dan
 * sinkronisasi token di layout selalu konsisten tanpa context bersama.
 *
 * Status:
 * - `loading`     : dukungan browser masih dicek.
 * - `unsupported` : browser/env tidak mendukung atau config Firebase kosong.
 * - `default`     : belum pernah diizinkan/ditolak.
 * - `granted`     : aktif.
 * - `opted_out`   : diizinkan browser, tapi dimatikan user di perangkat ini.
 * - `denied`      : diblokir di pengaturan browser.
 *
 * @returns {{
 *   status: "loading"|"unsupported"|"default"|"granted"|"opted_out"|"denied",
 *   pending: boolean,
 *   isPromptDismissed: boolean,
 *   enable: () => Promise<"default"|"granted"|"denied">,
 *   disable: () => Promise<void>,
 *   dismissPrompt: () => void,
 * }} State & aksi push.
 */
const usePushPermission = () => {
  const [supported, setSupported] = useState(null)
  const [permission, setPermission] = useState(getNotificationPermission)
  const [optedOut, setOptedOut] = useState(isPushOptedOut)
  const [promptDismissed, setPromptDismissed] = useState(() => isPushPromptDismissed())
  const [pending, setPending] = useState(false)

  useEffect(() => {
    let active = true
    isPushSupported().then((result) => {
      if (active) {
        setSupported(result)
      }
    })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const refresh = () => {
      setPermission(getNotificationPermission())
      setOptedOut(isPushOptedOut())
      setPromptDismissed(isPushPromptDismissed())
    }
    window.addEventListener(PUSH_STATE_EVENT, refresh)

    // Izin juga bisa diubah user lewat pengaturan situs browser (ikon gembok)
    // tanpa reload; `permissions.query` memberi tahu perubahan itu.
    let permissionStatus
    let active = true
    navigator.permissions
      ?.query({ name: 'notifications' })
      .then((result) => {
        if (!active) {
          return
        }
        permissionStatus = result
        permissionStatus.onchange = refresh
      })
      .catch(() => {})

    return () => {
      active = false
      window.removeEventListener(PUSH_STATE_EVENT, refresh)
      if (permissionStatus) {
        permissionStatus.onchange = null
      }
    }
  }, [])

  const enable = useCallback(async () => {
    setPending(true)
    try {
      setPushOptOut(false)
      // Native prompt dipanggil langsung dari klik user: Safari mewajibkan user
      // gesture dan Chrome meredam prompt yang muncul tanpa interaksi.
      if (getNotificationPermission() === 'granted') {
        return 'granted'
      }
      return await window.Notification.requestPermission()
    } finally {
      notifyPushStateChange()
      setPending(false)
    }
  }, [])

  const disable = useCallback(async () => {
    setPending(true)
    try {
      setPushOptOut(true)
      await teardownPushNotification()
    } finally {
      notifyPushStateChange()
      setPending(false)
    }
  }, [])

  const dismissPrompt = useCallback(() => {
    dismissPushPrompt()
    notifyPushStateChange()
  }, [])

  return {
    status: getStatus({ supported, permission, optedOut }),
    pending,
    isPromptDismissed: promptDismissed,
    enable,
    disable,
    dismissPrompt,
  }
}

export default usePushPermission
